import React, { useEffect, useMemo, useState } from "react";
import {
  deleteMessageContactForm,
  markMessageContactForm,
  getAllMessagesContactForm,
} from "../../../services/contactformServices";
import { useContactForm } from "../../../hooks/ContactFormContext";
import { MdMessage } from "react-icons/md";
import ButtonEyeNoRead from "../../../components/Button/ButtonEyeNoRead";
import ButtonEyeRead from "../../../components/Button/ButtonEyeRead";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import ButtonEnvelopeToggle from "../../../components/Button/ButtonEnvelopeToggle";
import { FaCheckDouble } from "react-icons/fa";
import { toast } from "react-toastify";

/** Normalise le champ read_message (0/1, "0"/"1", bool) en booléen */
function isReadFlag(v) {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") return v !== "0";
  return !!v;
}

export default function ContactFormDisplay({ userId, onCounts }) {
  const [messages, setMessages] = useState([]);
  const { setUnreadCountContactForm } = useContactForm();

  // Chargement
  useEffect(() => {
    const load = async () => {
      if (!userId) return;
      try {
        const data = await getAllMessagesContactForm(userId);
        setMessages(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Erreur chargement messages contact:", e);
        toast.error("Erreur lors du chargement des messages.");
      }
    };
    load();
  }, [userId]);

  // Totaux
  const { total, unread } = useMemo(() => {
    const t = messages.length;
    const u = messages.reduce((acc, m) => acc + (isReadFlag(m.read_message) ? 0 : 1), 0);
    return { total: t, unread: u };
  }, [messages]);

  // ➜ badge & parent (pas de boucle infinie)
  useEffect(() => {
    setUnreadCountContactForm(unread);
    if (onCounts) onCounts({ total, unread });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unread, total]);

  // Actions
  const toggleReadStatus = async (id) => {
    try {
      await markMessageContactForm(id);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === id ? { ...m, read_message: !isReadFlag(m.read_message) } : m
        )
      );
    } catch (e) {
      console.error(e);
      toast.error("Impossible de changer l'état de lecture.");
    }
  };

  const handleDeleteMessage = async (id) => {
    try {
      await deleteMessageContactForm(id);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success("Message supprimé.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la suppression.");
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const unreadIds = messages.filter((m) => !isReadFlag(m.read_message)).map((m) => m.id);
      if (unreadIds.length === 0) return;
      await Promise.all(unreadIds.map((id) => markMessageContactForm(id)));
      setMessages((prev) => prev.map((m) => ({ ...m, read_message: true })));
      toast.success("Tous les messages sont marqués comme lus.");
    } catch (e) {
      console.error(e);
      toast.error("Impossible de tout marquer comme lu.");
    }
  };

  return (
    <div className="space-y-4">
      {/* Titre + boutons compacts */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
          <div className="relative">
            <MdMessage className="text-xl" title="Messages du formulaire" />
            {unread > 0 && (
              <span className="absolute -top-2 -right-4 bg-violet-600 text-white text-[10px] font-bold rounded-full px-1.5 py-[1px]">
                {unread}
              </span>
            )}
          </div>
          <span className="text-xl font-bold ml-4">Messages du formulaire</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleMarkAllAsRead}
            className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs ring-1 ring-slate-300 dark:ring-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            title="Tout marquer comme lu"
          >
            <FaCheckDouble className="text-[12px]" />
            <span className="hidden sm:inline">Tout marquer lu</span>
          </button>
        </div>
      </div>

      {messages.length > 0 ? (
        <>
          {/* Desktop table */}
          <div className="hidden sm:block">
            <table className="min-w-full bg-white dark:bg-slate-900 divide-y divide-slate-200 dark:divide-slate-700 rounded-xl overflow-hidden text-[13px]">
              <thead className="bg-slate-50 dark:bg-slate-800">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 text-[11px]">
                    Date
                  </th>
                  <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 text-[11px]">
                    Détails
                  </th>
                  <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 text-[11px]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {messages.map((m) => {
                  const d = new Date(m.created_at);
                  const dateStr = d.toLocaleDateString("fr-FR", { year: "2-digit", month: "2-digit", day: "2-digit" });
                  const timeStr = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
                  const read = isReadFlag(m.read_message);

                  return (
                    <tr
                      key={m.id}
                      className={`${
                        read ? "bg-slate-50 dark:bg-slate-900/40" : "bg-white dark:bg-slate-900"
                      } hover:bg-slate-100 dark:hover:bg-slate-800`}
                    >
                      <td className="px-3 py-2">
                        <div className="text-xs font-medium text-slate-800 dark:text-slate-200">{dateStr}</div>
                        <div className="text-[10px] text-slate-500 dark:text-slate-400">{timeStr}</div>
                      </td>

                      <td className="px-3 py-2 text-slate-700 dark:text-slate-300 text-[13px] leading-snug">
                        <div className="font-semibold">{m.name}</div>
                        <div className="text-violet-700 dark:text-violet-300 font-bold italic">{m.email}</div>
                        <div className="mt-1">{m.message}</div>
                      </td>

                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {/* Tu peux remplacer par EyeRead/EyeNoRead si tu préfères */}
                          <ButtonEnvelopeToggle
                            onClick={() => toggleReadStatus(m.id)}
                            isRead={read}
                          />
                          <ButtonDelete
                            onClick={() => {
                              if (window.confirm("❗ Supprimer ce message de contact ?")) {
                                handleDeleteMessage(m.id);
                              }
                            }}
                            hoverLabel="Supprimer"
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="block sm:hidden">
            {messages.map((m) => {
              const d = new Date(m.created_at);
              const dateStr = d.toLocaleDateString("fr-FR", { year: "2-digit", month: "2-digit", day: "2-digit" });
              const timeStr = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
              const read = isReadFlag(m.read_message);

              return (
                <div
                  key={m.id}
                  className={`rounded-xl ring-1 overflow-hidden mb-3 p-3 ${
                    read
                      ? "bg-white/80 dark:bg-slate-900/60 ring-slate-200 dark:ring-slate-800"
                      : "bg-violet-50/80 dark:bg-violet-900/20 ring-violet-200 dark:ring-violet-800"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-xs font-semibold text-slate-800 dark:text-slate-200">{dateStr}</div>
                      <div className="text-[10px] text-slate-500 dark:text-slate-400">{timeStr}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <ButtonEnvelopeToggle
                        onClick={() => toggleReadStatus(m.id)}
                        isRead={read}
                      />
                      <ButtonDelete
                        onClick={() => handleDeleteMessage(m.id)}
                        hoverLabel="Supprimer"
                      />
                    </div>
                  </div>

                  <div className="mt-2 text-[13px] text-slate-700 dark:text-slate-300">
                    <div className="font-semibold">{m.name}</div>
                    <div className="text-violet-700 dark:text-violet-300 font-bold italic">{m.email}</div>
                    <div className="mt-1">{m.message}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <p className="text-center text-slate-600 dark:text-slate-300 text-sm">
          Aucuns messages.
        </p>
      )}
    </div>
  );
}
